import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertService, DialogService, NotifierService, TfaService, User } from '@wm-libs/core';
import { UserService } from '@wm-libs/core';
import { Subject, map, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PayoutsService } from '../../../../core/services/payouts.service';
import { Decimal } from 'decimal.js';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AccountsService } from '../../../../core/services/accounts.service';
import { isPlatformBrowser, NgClass, NgIf, AsyncPipe } from '@angular/common';
import { BalanceAndFee } from '../../../../core/interfaces/payouts.interface';
import { MatButtonModule } from '@angular/material/button';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogRef } from '@angular/material/dialog';
import { GenericDialogComponent } from '@wm-libs/core';

@Component({
  selector: 'app-withdraw',
  templateUrl: './withdraw.component.html',
  styleUrls: ['./withdraw.component.scss'],
  standalone: true,
  imports: [NgClass, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, NgxMaskDirective, NgIf, MatButtonModule, AsyncPipe, TranslateModule],
  providers: [provideNgxMask({ thousandSeparator: ',', decimalMarker: '.' })],
})
export class WithdrawComponent implements OnInit, OnDestroy {
  userData$!: User | null;

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));
  isSmallMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall]).pipe(map((result) => result.matches));

  paymentAddressForm!: FormGroup;
  balanceAndFee: BalanceAndFee | null = null;
  totalAmount!: Decimal | number;
  intervalId!: ReturnType<typeof setInterval>;

  toggleSeeMore = true;

  maxAmount!: number;
  balanceAndFeeLoaded = false;

  @ViewChild(FormGroupDirective, { static: false })
  formGroupDirective!: FormGroupDirective;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private payoutsService: PayoutsService,
    private fb: FormBuilder,
    private alertService: AlertService,
    private breakpointObserver: BreakpointObserver,
    private userService: UserService,
    public dialog: MatDialog,
    private translateService: TranslateService,
    private accountsService: AccountsService,
    private notifier: NotifierService,
    private dialogService: DialogService,
    private tfaService: TfaService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    this.userService.userData$.pipe(takeUntil(this._unsubscribe$)).subscribe((res) => {
      this.userData$ = res;
    });

    this.paymentAddressForm = this.fb.group({
      btcPaymentAddress: ['', [Validators.required]],
      amount: [null, [Validators.required, Validators.min(0.0001)]],
    });

    this.notifier.notifications$.pipe(takeUntil(this._unsubscribe$)).subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED' || result === 'POOL.PAYOUT_UPDATED') {
        this.balanceAndFee = null;
        this.balanceAndFeeLoaded = false;
        this.notifier.clearNotifications();
        this.getBanalceAndFee();
      }
    });
  }

  ngOnInit() {
    this.getBanalceAndFee();

    this.paymentAddressForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe((value) => {
      if (value?.amount > 0) {
        this.paymentAddressForm.get('btcPaymentAddress')?.markAllAsTouched();
      }

      if (value?.amount > 0) {
        if (this.balanceAndFee?.fee) {
          this.totalAmount = Decimal.sub(value.amount, -this.balanceAndFee?.fee).toNumber();
        }
      } else {
        this.totalAmount = 0;
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      this.intervalId = setInterval(() => {
        this.getBanalceAndFee();
      }, 600000);
    }
  }

  getBanalceAndFee() {
    setTimeout(() => this.formGroupDirective.resetForm(), 100);

    this.payoutsService.getBanalceAndFee().subscribe((res) => {
      this.accountsService.updateCurrentAccountBalance(res.balance);

      this.balanceAndFee = res;
      this.balanceAndFeeLoaded = true;

      this.maxAmount = Decimal.sub(this.balanceAndFee.availableBalance, this.balanceAndFee.fee).toNumber();

      this.paymentAddressForm?.get('amount')?.clearValidators();
      this.paymentAddressForm?.get('amount')?.setValidators([Validators.required, Validators.min(0.0001), Validators.max(this.maxAmount)]);
    });
  }

  preWithdraw() {
    if (this.balanceAndFee?.isNormalTransactionFeePrice) {
      this.withdraw();
    } else {
      const config = {
        title: 'WM_TECH_POOL.WITHDRAW.FEE_ALERT_TITLE',
        app: 'pool',
        submitLabel: 'WM_TECH.DIALOG.OK',
      };

      const dialogRef = this.dialogService.openPopup(config);

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.withdraw();
        }
      });
    }
  }

  withdraw() {
    if (this.paymentAddressForm.valid) {
      if (!this.userData$?.tfaEnabled) {
        this.tfaService.enableTfa().subscribe((result) => {
          if (result) {
            this.openWithdrawDialog();
          }
        });
      } else {
        this.openWithdrawDialog();
      }
    } else {
      this.alertService.showAlert(this.translateService.instant('FORMS.NOT_VALID'), 'error');
    }
  }

  openWithdrawDialog() {
    const config = {
      image: 'assets/images/t2a/google_authenticator.svg',
      imageAlt: 'tfa',
      title: 'WM_TECH_POOL.TFA.DIALOG.TITLE',
      app: 'pool',
      fields: [{ name: 'tfaCode', label: 'WM_TECH_POOL.WITHDRAW.DIALOG.ENTER_CODE', type: 'input', validators: [Validators.required] }],
      autoClose: false,
    };

    const dialogRef = this.dialogService.openPopup(config);

    dialogRef?.componentInstance?.formSubmited?.subscribe((result) => {
      if (result?.submited && result?.formValue?.tfaCode) {
        const code = result?.formValue?.tfaCode;
        this.confirmWithdraw(code.toString(), dialogRef);
      }
    });
  }

  confirmWithdraw(code: string, dialogRef: MatDialogRef<GenericDialogComponent>) {
    const { btcPaymentAddress, amount } = this.paymentAddressForm.value;

    if (btcPaymentAddress && amount && code) {
      this.payoutsService.createPayouts(btcPaymentAddress, amount, code).subscribe(
        () => {
          dialogRef.close();

          this.alertService.showAlert(this.translateService.instant('WITHDRAW.PENDING'), 'success');

          this.payoutsService.getPayouts().subscribe((res) => {
            this.payoutsService.setInitPayouts(res);
          });
        },
        (error) => {
          if (error.errorCode === 'TFA_INVALID_CODE_ERROR') {
            dialogRef?.componentInstance?.form?.reset();
          } else if (error.errorCode === 'OUTDATED_TRANSACTION_FEE') {
            this.getBanalceAndFee();
            dialogRef.close({ reset: true });
          } else if (error.errorCode === 'INVALID_WALLET_ADDRESS') {
            dialogRef?.componentInstance?.form?.reset();
          } else {
            dialogRef.close();
          }
        }
      );
    }
  }

  setMaxValueForWithdraw(event: Event) {
    if (this.balanceAndFee && this.maxAmount < this.balanceAndFee?.fee) {
      event.preventDefault();
    } else {
      this.paymentAddressForm.patchValue({
        amount: this.maxAmount,
      });
    }
  }

  getErrorMessage() {
    if (this.paymentAddressForm?.value?.amount < 0.0001) {
      return this.translateService.instant('WITHDRAW.MIN_AMOUNT_ERROR');
    } else if (this.paymentAddressForm?.value?.amount > this.maxAmount) {
      return this.translateService.instant('WITHDRAW.MAX_AMOUNT_ERROR');
    }
    return '';
  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    clearInterval(this.intervalId);
  }
}
